import * as React from "react";
import { useEffect, useState } from "react";
import capitalize from "lodash/capitalize";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useStore } from "stores/connect";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { getConfig } from "components/configurations/helpers";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { Button, FormGroup, HelpBlock, BootstrapModalForm } from "components/bootstrap";
import { IfPermitted, TimeUnitInput } from "components/common";
import Spinner from "components/common/Spinner";
import { getValueFromInput } from "util/FormsUtils";
import Input from "components/bootstrap/Input";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
const TIME_UNITS = ["HOURS", "MINUTES", "SECONDS"];
const DEFAULT_CATCH_UP_WINDOW = 36e5;
const DEFAULT_CONFIG = {
  events_search_timeout: 6e4,
  events_notification_retry_period: 3e5,
  events_notification_default_backlog: 50,
  events_catchup_window: DEFAULT_CATCH_UP_WINDOW,
  events_notification_tcp_keepalive: false
};
const EventsConfig = () => {
  const { t } = useTranslation("configurations");
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(DEFAULT_CONFIG);
  const [formConfig, setFormConfig] = useState(DEFAULT_CONFIG);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    ConfigurationsActions.list(ConfigurationType.EVENTS_CONFIG).then(() => {
      const config = getConfig(ConfigurationType.EVENTS_CONFIG, configuration);
      setViewConfig(config);
      setFormConfig(config);
      setLoaded(true);
    });
  }, [configuration]);
  const openModal = () => {
    setShowConfigModal(true);
  };
  const closeModal = () => {
    setShowConfigModal(false);
    setFormConfig(viewConfig);
  };
  const saveConfig = () => {
    ConfigurationsActions.update(ConfigurationType.EVENTS_CONFIG, formConfig).then(() => {
      closeModal();
    });
  };
  const propagateChanges = (key, value) => {
    setFormConfig({ ...formConfig, [key]: value });
  };
  const searchTimeoutValidator = (milliseconds) => milliseconds >= 1e3;
  const notificationsRetryValidator = (milliseconds) => milliseconds >= 0;
  const onSearchTimeoutUpdate = (nextValue, nextUnit, enabled) => {
    const durationInMs = enabled ? moment.duration(nextValue, nextUnit).asMilliseconds() : 0;
    if (searchTimeoutValidator(durationInMs)) {
      propagateChanges("events_search_timeout", durationInMs);
    }
  };
  const onRetryPeriodUpdate = (nextValue, nextUnit, enabled) => {
    const durationInMs = enabled ? moment.duration(nextValue, nextUnit).asMilliseconds() : 0;
    if (notificationsRetryValidator(durationInMs)) {
      propagateChanges("events_notification_retry_period", durationInMs);
    }
  };
  const onBacklogUpdate = (event) => {
    const value = getValueFromInput(event.target);
    propagateChanges("events_notification_default_backlog", value);
  };
  const onCatchUpWindowUpdate = (nextValue, nextUnit, nextEnabled) => {
    if (formConfig.events_catchup_window === 0 && nextEnabled) {
      propagateChanges("events_catchup_window", DEFAULT_CATCH_UP_WINDOW);
      return;
    }
    const catchupWindowinMs = nextEnabled ? moment.duration(nextValue, nextUnit).asMilliseconds() : 0;
    propagateChanges("events_catchup_window", catchupWindowinMs);
  };
  const onNotificationTcpKeepAliveUpdate = (event) => {
    const value = getValueFromInput(event.target);
    propagateChanges("events_notification_tcp_keepalive", value);
  };
  const unitLabel = (unit) => capitalize(t(`common:time_units.${unit.toLowerCase()}`));
  if (!loaded || !viewConfig) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const eventsSearchTimeout = (config) => extractDurationAndUnit(config.events_search_timeout, TIME_UNITS);
  const eventsNotificationRetryPeriod = (config) => extractDurationAndUnit(config.events_notification_retry_period, TIME_UNITS);
  const eventsCatchupWindow = (config) => extractDurationAndUnit(config.events_catchup_window, TIME_UNITS);
  const eventsNotificationDefaultBacklog = (config) => config.events_notification_default_backlog;
  const eventsNotificationTcpKeepalive = (config) => config.events_notification_tcp_keepalive;
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, t("events.title")), /* @__PURE__ */ React.createElement("dl", { className: "deflist" }, /* @__PURE__ */ React.createElement("dt", null, t("events.search_timeout"), ":"), /* @__PURE__ */ React.createElement("dd", null, eventsSearchTimeout(viewConfig).duration, " ", unitLabel(eventsSearchTimeout(viewConfig).unit)), /* @__PURE__ */ React.createElement("dt", null, t("events.notification_retry"), ":"), /* @__PURE__ */ React.createElement("dd", null, eventsNotificationRetryPeriod(viewConfig).duration, " ", unitLabel(eventsNotificationRetryPeriod(viewConfig).unit)), /* @__PURE__ */ React.createElement("dt", null, t("events.notification_backlog"), ":"), /* @__PURE__ */ React.createElement("dd", null, eventsNotificationDefaultBacklog(viewConfig)), /* @__PURE__ */ React.createElement("dt", null, t("events.catch_up_window"), ":"), /* @__PURE__ */ React.createElement("dd", null, eventsCatchupWindow(viewConfig).duration > 0 ? eventsCatchupWindow(viewConfig).duration : t("shared.disabled"), " ", eventsCatchupWindow(viewConfig).duration > 0 ? unitLabel(eventsCatchupWindow(viewConfig).unit) : ""), /* @__PURE__ */ React.createElement("dt", null, t("events.tcp_keepalive_probes"), ":"), /* @__PURE__ */ React.createElement("dd", null, eventsNotificationTcpKeepalive(viewConfig) ? t("shared.enabled") : t("shared.disabled"))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(Button, { bsStyle: "info", bsSize: "xs", onClick: openModal }, t("shared.edit_configuration"))), showConfigModal && formConfig && /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      show: true,
      title: t("events.update_title"),
      onSubmitForm: saveConfig,
      onCancel: closeModal,
      submitButtonText: t("shared.update_configuration")
    },
    /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-timeout-field" }, /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: t("events.search_timeout"),
        update: onSearchTimeoutUpdate,
        value: eventsSearchTimeout(formConfig).duration,
        unit: eventsSearchTimeout(formConfig).unit,
        units: TIME_UNITS,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("events.search_timeout_help"))), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notifications-retry-field" }, /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: t("events.retry_period"),
        update: onRetryPeriodUpdate,
        value: eventsNotificationRetryPeriod(formConfig).duration,
        unit: eventsNotificationRetryPeriod(formConfig).unit,
        units: TIME_UNITS,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("events.retry_period_help"))), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-backlog-field",
        type: "number",
        onChange: onBacklogUpdate,
        label: t("events.backlog_size"),
        help: t("events.backlog_size_help"),
        value: eventsNotificationDefaultBacklog(formConfig),
        min: "0",
        required: true
      }
    ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "catch-up-window" }, /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: t("events.catch_up_window_size"),
        update: onCatchUpWindowUpdate,
        value: eventsCatchupWindow(formConfig).duration,
        unit: eventsCatchupWindow(formConfig).unit,
        enabled: eventsCatchupWindow(formConfig).duration > 0,
        units: TIME_UNITS
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("events.catch_up_window_help"))), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-tcp-keepalive-field" }, /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-tcp-keepalive-field",
        label: t("events.tcp_keepalive_label"),
        type: "checkbox",
        onChange: onNotificationTcpKeepAliveUpdate,
        checked: eventsNotificationTcpKeepalive(formConfig)
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("events.tcp_keepalive_help"))))
  ));
};
export default EventsConfig;
